import { BaseQueryFn, FetchArgs, fetchBaseQuery } from "@reduxjs/toolkit/query"
import { z } from "zod"
import { RootState } from "../store"
import { NormalizedErrorResult, normalizeApiErrorResponse } from "./error-result"
import type { NormalizedError } from "./error-result"

export type { NormalizedError } from "./error-result"

const JsonSuccessResponseSchema = z.object({
  success: z.literal(true),
  data: z.unknown(),
})

const ErrorResult = {
  unknownError: (message?: string) => ({ error: NormalizedErrorResult.unknownError(message) }),
  unexpectedResponseShape: () => ({ error: NormalizedErrorResult.unexpectedResponseShape() }),
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState
    const accessToken = state.auth.accessToken
    if (accessToken) {
      headers.set("authorization", `Bearer ${accessToken}`)
    }
    return headers
  },
})

export const customBaseQuery: BaseQueryFn<string | FetchArgs, unknown, NormalizedError> = async (
  args,
  api,
  extraOptions
) => {
  const result = await rawBaseQuery(args, api, extraOptions)

  if (result.error) {
    if (typeof result.error === "string") {
      // @ts-expect-error - handle error strings
      return ErrorResult.unknownError(result.error as string)
    }

    // Handle structured error responses, must conform to JsonErrorResponseSchema
    return { error: normalizeApiErrorResponse(result.error.data) }
  }

  // Handle 204, no content
  if (result.data === null) return { data: null }

  // Handle text responses
  if (typeof result.data === "string") return { data: result.data }

  // Handle JSON responses, must conform to JsonSuccessResponseSchema
  const parsed = JsonSuccessResponseSchema.safeParse(result.data)
  if (!parsed.success) {
    return ErrorResult.unexpectedResponseShape()
  }

  return { data: parsed.data.data }
}
